import * as productApi from '@apis/product';
import * as productCollectionApi from '@apis/product-collection';
import { DiscountCoupon } from '@models/discount-coupon';
import { ProductModel } from '@models/product';
import { ProductCollection } from '@models/productCollection';
import { useQuery } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import { Button, Modal, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React from 'react';

interface ApplicabilityDetailModalProps {
  open?: boolean;
  onClose?: () => void;
  coupon?: DiscountCoupon;
}

export const ApplicabilityDetailModal: React.FC<ApplicabilityDetailModalProps> = (props) => {
  const isProductsType = props.coupon?.applicability?.type === 'products';
  const isCollectionsType = props.coupon?.applicability?.type === 'collections';

  // 상품 목록 로드
  const productIds = props.coupon?.applicability?.productIds || [];
  const productsQuery = useQuery({
    queryKey: ['applicability-products', productIds],
    queryFn: async () => {
      if (productIds.length === 0) return [];
      const promises = productIds.map((id) => productApi.getProduct(id));
      const results = await Promise.all(promises);
      return results.map((result) => result.data).filter(Boolean);
    },
    enabled: isProductsType && productIds.length > 0,
  });

  // 기획전 목록 로드
  const collectionIds = props.coupon?.applicability?.collections?.map((c) => c.id) || [];
  const collectionsQuery = useQuery({
    queryKey: ['applicability-collections', collectionIds],
    queryFn: async () => {
      if (collectionIds.length === 0) return [];
      const promises = collectionIds.map((id: string) => productCollectionApi.getProductCollection(id));
      const results = await Promise.all(promises);
      return results.map((result) => result.data).filter(Boolean);
    },
    enabled: isCollectionsType && collectionIds.length > 0,
  });

  const PRODUCT_COLUMNS: ColumnsType<ProductModel> = [
    {
      title: '썸네일',
      dataIndex: 'thumbnailImage',
      width: 80,
      render: (thumbnailImage) => (
        <img
          src={cdnImageUrl(thumbnailImage)}
          style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
          alt="상품 썸네일"
        />
      ),
    },
    {
      title: '상품명',
      dataIndex: 'title',
      width: 300,
      render: (title) => <div style={{ fontWeight: 'bold' }}>{title}</div>,
    },
    {
      title: '판매방식',
      dataIndex: 'saleType',
      width: 100,
      render: (saleType) => (
        <Tag color={saleType === 'class' ? 'blue' : 'green'}>{saleType === 'class' ? '클래스' : '커머스'}</Tag>
      ),
    },
    {
      title: '금액',
      dataIndex: 'fee',
      width: 120,
      render: (fee) => `${fee?.toLocaleString()}원`,
    },
    {
      title: '판매자',
      dataIndex: 'sellerName',
      width: 150,
      render: (sellerName) => sellerName,
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      width: 120,
      render: (createdAt) => moment(createdAt).format('YYYY.MM.DD'),
    },
  ];

  const COLLECTION_COLUMNS: ColumnsType<ProductCollection> = [
    {
      title: '커버 이미지',
      dataIndex: 'coverImageUrl',
      width: 100,
      render: (coverImageUrl) => (
        <img
          src={cdnImageUrl(coverImageUrl)}
          style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
          alt="기획전 커버"
        />
      ),
    },
    {
      title: '기획전명',
      dataIndex: 'title',
      width: 250,
      render: (title) => <div style={{ fontWeight: 'bold' }}>{title}</div>,
    },
    {
      title: '타입',
      dataIndex: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'general' ? 'blue' : type === 'md_pick' ? 'purple' : 'green'}>
          {type === 'general' ? '일반형' : type === 'md_pick' ? 'MD PICK' : type}
        </Tag>
      ),
    },
    {
      title: '상품 수',
      dataIndex: 'products',
      width: 100,
      render: (products) => `${products?.length || 0}개`,
    },
    {
      title: '설명',
      dataIndex: 'description',
      width: 200,
      render: (description) => description || '-',
    },
  ];

  const getModalTitle = () => {
    if (isProductsType) return `적용 상품 목록 (${productIds.length}개)`;
    if (isCollectionsType) return `적용 기획전 목록 (${collectionIds.length}개)`;
    return '적용 범위 상세';
  };

  const getModalContent = () => {
    if (isProductsType) {
      if (productIds.length === 0) {
        return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>적용된 상품이 없습니다.</div>;
      }
      return (
        <Table
          columns={PRODUCT_COLUMNS}
          dataSource={productsQuery.data || []}
          rowKey="_id"
          loading={productsQuery.isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      );
    }

    if (isCollectionsType) {
      if (collectionIds.length === 0) {
        return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>적용된 기획전이 없습니다.</div>;
      }
      return (
        <Table
          columns={COLLECTION_COLUMNS}
          dataSource={collectionsQuery.data || []}
          rowKey="_id"
          loading={collectionsQuery.isLoading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      );
    }

    return <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>전체 상품에 적용됩니다.</div>;
  };

  return (
    <Modal
      title={getModalTitle()}
      open={props.open}
      onCancel={props.onClose}
      footer={<Button onClick={props.onClose}>닫기</Button>}
      width={isProductsType ? 1000 : 800}
      styles={{ body: { maxHeight: '600px', overflow: 'auto' } }}>
      {getModalContent()}
    </Modal>
  );
};

export default ApplicabilityDetailModal;
